import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import React, { useEffect, useState, useCallback } from 'react';

export default function GalleryView(props) {
    const { photos = [] } = props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);

    const nextImage = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % photos.length);
    }, [photos.length]);

    const prevImage = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
    }, [photos.length]);

    const pauseAutoScroll = useCallback(() => {
        setIsAutoScrollPaused(true);
    }, []);

    // Auto-scroll every 5 seconds, but pause if user interacts
    useEffect(() => {
        if (photos.length <= 1 || isAutoScrollPaused) return;
        
        const interval = setInterval(nextImage, 5000);
        return () => clearInterval(interval);
    }, [nextImage, photos.length, isAutoScrollPaused]);

    // Handle touch events for swipe
    const handleTouchStart = (e) => {
        pauseAutoScroll();
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const minSwipeDistance = 50;

        if (distance > minSwipeDistance) {
            nextImage();
        } else if (distance < -minSwipeDistance) {
            prevImage();
        }
    };

    const handlePrevClick = () => {
        pauseAutoScroll();
        prevImage();
    };

    const handleNextClick = () => {
        pauseAutoScroll();
        nextImage();
    };

    const handleDotClick = (index) => {
        pauseAutoScroll();
        setCurrentIndex(index);
    };

    if (!photos || photos.length === 0) {
        return (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: 300,
                backgroundColor: '#f5f5f5',
                borderRadius: 2
            }}>
                No images to display
            </Box>
        );
    }

    return (
        <Box sx={{ 
            position: 'relative', 
            width: '100%', 
            maxWidth: 1024,
            height: 550,
            margin: '0 auto',
            backgroundColor: '#00000000',
            borderRadius: 2,
            overflow: 'hidden'
        }}>
            <Box
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 500,
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing'
                    }
                }}
            >
                <img
                    src={photos[currentIndex]}
                    alt={`Gallery image ${currentIndex + 1}`}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block'
                    }}
                />

            </Box>

            {photos.length > 1 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    padding: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0)'
                }}>
                    <IconButton
                        onClick={handlePrevClick}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {photos.map((_, index) => (
                            <Box
                                key={index}
                                onClick={() => handleDotClick(index)}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: index === currentIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                    cursor: 'pointer',
                                    transition: 'backgroundColor 0.3s ease'
                                }}
                            />
                        ))}
                    </Box>

                    <IconButton
                        onClick={handleNextClick}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}


